import React, { useState, useRef, useEffect } from 'react';
import {
  Plus,
  Search,
  Trash2,
  PanelLeftClose,
  PanelLeftOpen,
  GitCompare,
} from 'lucide-react';
import type { LLMSessionSummary, LLMMatchingStatus } from '../../services/api';
import type { ContractOption } from './ContractSelector';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AnalysisHistorySidebarProps {
  sessions: LLMSessionSummary[];
  activeSessionId: string | null;
  onSelectSession: (id: string) => void;
  onNewAnalysis: () => void;
  onDeleteSession: (id: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  contracts: ContractOption[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function relativeDate(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays <= 7) return 'Previous 7 days';
  return 'Older';
}

function groupByDate(sessions: LLMSessionSummary[]): Map<string, LLMSessionSummary[]> {
  const groups = new Map<string, LLMSessionSummary[]>();
  for (const session of sessions) {
    const group = relativeDate(session.updatedAt);
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group)!.push(session);
  }
  return groups;
}

function resolveContractName(contractId: string, contracts: ContractOption[]): string {
  return contracts.find((c) => c.id === contractId)?.name ?? contractId.slice(0, 8) + '...';
}

function statusLabel(status: LLMMatchingStatus): { text: string; color: string } {
  switch (status) {
    case 'completed':
      return { text: 'Done', color: 'bg-green-100 text-green-700' };
    case 'awaiting_review':
      return { text: 'Review', color: 'bg-yellow-100 text-yellow-700' };
    case 'section_matching':
    case 'toc_matching':
      return { text: 'Running', color: 'bg-blue-100 text-blue-700' };
    case 'failed':
      return { text: 'Failed', color: 'bg-red-100 text-red-700' };
    default:
      return { text: status, color: 'bg-gray-100 text-gray-600' };
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AnalysisHistorySidebar: React.FC<AnalysisHistorySidebarProps> = ({
  sessions,
  activeSessionId,
  onSelectSession,
  onNewAnalysis,
  onDeleteSession,
  isCollapsed,
  onToggleCollapse,
  contracts,
}) => {
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [filterText, setFilterText] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [debouncedFilter, setDebouncedFilter] = useState('');

  // Clear delete confirm when clicking elsewhere
  useEffect(() => {
    if (!deleteConfirmId) return;
    const timer = setTimeout(() => setDeleteConfirmId(null), 3000);
    return () => clearTimeout(timer);
  }, [deleteConfirmId]);

  const handleFilterChange = (value: string) => {
    setFilterText(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedFilter(value);
    }, 300);
  };

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (deleteConfirmId === id) {
      onDeleteSession(id);
      setDeleteConfirmId(null);
    } else {
      setDeleteConfirmId(id);
    }
  };

  // Client-side filter by contract name
  const filteredSessions = debouncedFilter
    ? sessions.filter((s) => {
        const baseline = resolveContractName(s.baselineContractId, contracts).toLowerCase();
        const target = resolveContractName(s.targetContractId, contracts).toLowerCase();
        const q = debouncedFilter.toLowerCase();
        return baseline.includes(q) || target.includes(q);
      })
    : sessions;

  // Collapsed state: show toggle button only
  if (isCollapsed) {
    return (
      <div className="flex flex-col items-center py-3 px-1 border-r border-secondary-200 bg-white">
        <button
          onClick={onToggleCollapse}
          className="p-2 rounded-md text-secondary-500 hover:bg-secondary-100 hover:text-secondary-700 transition-colors"
          title="Expand sidebar"
        >
          <PanelLeftOpen className="h-5 w-5" />
        </button>
        <button
          onClick={onNewAnalysis}
          className="mt-2 p-2 rounded-md text-primary-600 hover:bg-primary-50 transition-colors"
          title="New analysis"
        >
          <Plus className="h-5 w-5" />
        </button>
      </div>
    );
  }

  const grouped = groupByDate(filteredSessions);

  return (
    <div className="flex flex-col w-72 border-r border-secondary-200 bg-white h-full" data-testid="analysis-sidebar">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-3 border-b border-secondary-200">
        <button
          onClick={onNewAnalysis}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors"
          data-testid="new-analysis-button"
        >
          <Plus className="h-4 w-4" />
          New Analysis
        </button>
        <button
          onClick={onToggleCollapse}
          className="p-1.5 rounded-md text-secondary-400 hover:bg-secondary-100 hover:text-secondary-600 transition-colors"
          title="Collapse sidebar"
        >
          <PanelLeftClose className="h-4 w-4" />
        </button>
      </div>

      {/* Search */}
      <div className="px-3 py-2 border-b border-secondary-100">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-secondary-400" />
          <input
            type="text"
            placeholder="Filter by contract name..."
            value={filterText}
            onChange={(e) => handleFilterChange(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md border border-secondary-200 bg-secondary-50 text-secondary-700 placeholder-secondary-400 focus:border-primary-400 focus:ring-1 focus:ring-primary-400 focus:bg-white"
            data-testid="analysis-search"
          />
        </div>
      </div>

      {/* Session list */}
      <div className="flex-1 overflow-y-auto">
        {filteredSessions.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-secondary-400 px-4">
            <GitCompare className="h-8 w-8 mb-2 opacity-40" />
            <p className="text-xs text-center">
              {debouncedFilter ? 'No analyses match your filter' : 'No analyses yet'}
            </p>
          </div>
        ) : (
          Array.from(grouped.entries()).map(([group, groupSessions]) => (
            <div key={group}>
              <div className="px-3 pt-3 pb-1">
                <span className="text-xs font-semibold text-secondary-400 uppercase tracking-wide">
                  {group}
                </span>
              </div>
              {groupSessions.map((session) => (
                <SessionItem
                  key={session.id}
                  session={session}
                  isActive={session.id === activeSessionId}
                  contracts={contracts}
                  deleteConfirmId={deleteConfirmId}
                  onSelect={onSelectSession}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Session list item
// ---------------------------------------------------------------------------

const SessionItem: React.FC<{
  session: LLMSessionSummary;
  isActive: boolean;
  contracts: ContractOption[];
  deleteConfirmId: string | null;
  onSelect: (id: string) => void;
  onDelete: (e: React.MouseEvent, id: string) => void;
}> = ({ session, isActive, contracts, deleteConfirmId, onSelect, onDelete }) => {
  const baselineName = resolveContractName(session.baselineContractId, contracts);
  const targetName = resolveContractName(session.targetContractId, contracts);
  const time = new Date(session.updatedAt);
  const timeStr = time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const badge = statusLabel(session.status);

  return (
    <button
      onClick={() => onSelect(session.id)}
      className={`group w-full text-left px-3 py-2.5 transition-colors ${
        isActive
          ? 'bg-primary-50 border-r-2 border-primary-500'
          : 'hover:bg-secondary-50'
      }`}
      data-testid={`session-${session.id}`}
    >
      <div className="flex items-start justify-between gap-1">
        <div className="flex-1 min-w-0">
          <p
            className={`text-sm truncate ${
              isActive ? 'font-semibold text-primary-800' : 'font-medium text-secondary-800'
            }`}
          >
            {baselineName}
          </p>
          <p className="text-xs text-secondary-400 truncate mt-0.5">
            vs {targetName}
          </p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${badge.color}`}>
            {badge.text}
          </span>
          <button
            onClick={(e) => onDelete(e, session.id)}
            className={`p-1 rounded transition-colors ${
              deleteConfirmId === session.id
                ? 'text-red-600 bg-red-50'
                : 'text-secondary-300 opacity-0 group-hover:opacity-100 hover:text-red-500 hover:bg-red-50'
            }`}
            title={deleteConfirmId === session.id ? 'Click again to confirm' : 'Delete analysis'}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div className="flex items-center justify-between mt-1">
        <span className="text-xs text-secondary-400">{timeStr}</span>
      </div>
    </button>
  );
};

export default AnalysisHistorySidebar;
